/**
 * <events-grid> — client-side filtering, search and sorting for the events product grid.
 *
 * Filter CONTROLS are rendered server-side (snippets/events-filter-controls.liquid) so they always
 * appear. Each card carries its variant metafield data in a <script type="application/json"
 * data-events-data> child. This component wires interaction + matching, eager-loads any remaining
 * paginated pages, and (for collections larger than one page) supplements list options with values
 * found in the eager-loaded cards.
 *
 * Matching: OR within a list group, AND across groups; numeric range = any variant value in [min,max];
 * date range = any variant date in [from,to]; plus free-text search. No server round-trips.
 */
(function () {
  'use strict';

  // The client filter assumes the grid starts on page 1 (it eager-loads pages 2..N on top of it).
  // If deep-linked / back-navigated to ?page=N (N>=2), redirect to page 1 first (preserving ev_* params).
  (function () {
    try {
      var p = new URLSearchParams(location.search);
      if (parseInt(p.get('page'), 10) >= 2) {
        p.delete('page');
        var qs = p.toString();
        location.replace(location.pathname + (qs ? '?' + qs : ''));
      }
    } catch (e) { /* noop */ }
  })();

  var DATE_KEYS = ['date', 'start_time_'];

  var debounceFn = (typeof debounce === 'function')
    ? debounce
    : function (fn, wait) {
        var t;
        return function () {
          var ctx = this, args = arguments;
          clearTimeout(t);
          t = setTimeout(function () { fn.apply(ctx, args); }, wait);
        };
      };

  function normalize(v) {
    return (v == null ? '' : String(v)).trim().toLowerCase();
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function escapeAttr(s) {
    return escapeHtml(s).replace(/"/g, '&quot;');
  }

  function formatDate(iso) {
    var parts = String(iso).split('-');
    if (parts.length !== 3) return iso;
    var y = parseInt(parts[0], 10), m = parseInt(parts[1], 10), d = parseInt(parts[2], 10);
    if (!y || !m || !d) return iso;
    var dt = new Date(y, m - 1, d);
    if (isNaN(dt.getTime())) return iso;
    try {
      return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return iso;
    }
  }

  class EventsGrid extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.layout = this.dataset.layout || 'sidebar';
      this.defaultSort = this.dataset.defaultSort || 'featured';
      this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
      this.baseUrl = this.dataset.baseUrl || location.pathname;

      this.grid = this.querySelector('#events-grid-ajax');
      this.searchInput = this.querySelector('[data-events-search-input]');
      this.sortSelect = this.querySelector('[data-events-sort]');
      this.chipsEl = this.querySelector('[data-events-chips]');
      this.countEl = this.querySelector('[data-events-count]');
      this.emptyEl = this.querySelector('[data-events-empty]');

      this.state = { filters: {}, ranges: {}, dateRanges: {}, search: '', searchRaw: '', sort: this.defaultSort };
      this.cards = null;
      this.optionLabels = {}; // key -> { norm: labelText }

      if (!this.grid) return;
      this.init();
    }

    async init() {
      this.classList.add('is-loading');
      this.collect();
      this.readControls();
      this.wireEvents();
      this.readURL();
      this.updateChips();
      this.apply();
      await this.eagerLoad();
      this.classList.remove('is-loading');
    }

    async eagerLoad() {
      if (this.totalPages <= 1) return;
      var sep = this.baseUrl.indexOf('?') === -1 ? '?' : '&';
      for (var p = 2; p <= this.totalPages; p++) {
        try {
          var res = await fetch(this.baseUrl + sep + 'page=' + p, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!res.ok) continue;
          var text = await res.text();
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var g = doc.getElementById('events-grid-ajax');
          if (!g) continue;
          g.querySelectorAll('.card--banner').forEach(function (b) { b.remove(); });
          this.grid.insertAdjacentHTML('beforeend', g.innerHTML);
          this.refresh();
        } catch (e) { /* keep what loaded */ }
      }
    }

    refresh() {
      this.collect();
      this.supplementOptions();
      this.recomputeBounds();
      this.reassertChecked();
      this.updateChips();
      this.apply();
    }

    /* For collections > 1 page: widen range / date-range input bounds to the full eager-loaded set
       (server bounds reflect page 1 only). Matching is unaffected — this only fixes advisory bounds. */
    recomputeBounds() {
      var self = this;
      this.keys.forEach(function (k) {
        var ftype = self.ftypes[k] || 'list';
        if (ftype === 'range') {
          var min = null, max = null;
          self.cards.forEach(function (c) {
            (c._data[k] || []).forEach(function (v) {
              var n = Number(v);
              if (isNaN(n)) return;
              if (min === null || n < min) min = n;
              if (max === null || n > max) max = n;
            });
          });
          if (min === null) return;
          Array.prototype.forEach.call(self.querySelectorAll('[data-events-numrange][data-key="' + k + '"]'), function (w) {
            w.setAttribute('data-min', min); w.setAttribute('data-max', max);
          });
          Array.prototype.forEach.call(self.querySelectorAll('[data-events-range-min][data-key="' + k + '"]'), function (i) { i.min = min; i.max = max; i.placeholder = min; });
          Array.prototype.forEach.call(self.querySelectorAll('[data-events-range-max][data-key="' + k + '"]'), function (i) { i.min = min; i.max = max; i.placeholder = max; });
        } else if (ftype === 'date_range') {
          var dmin = null, dmax = null;
          self.cards.forEach(function (c) {
            (c._data[k] || []).forEach(function (d) {
              if (!d) return;
              if (dmin === null || d < dmin) dmin = d;
              if (dmax === null || d > dmax) dmax = d;
            });
          });
          if (dmin === null) return;
          Array.prototype.forEach.call(self.querySelectorAll('[data-events-daterange-from][data-key="' + k + '"],[data-events-daterange-to][data-key="' + k + '"]'), function (i) { i.min = dmin; i.max = dmax; });
        }
      });
    }

    /* Incremental: parse only newly-seen cards so _order stays stable in collection order. */
    collect() {
      if (!this.cards) { this.cards = []; this._seen = new Set(); this._orderCounter = 0; }
      var self = this;
      Array.prototype.forEach.call(this.grid.querySelectorAll('[data-events-card]'), function (c) {
        if (self._seen.has(c)) return;
        self._seen.add(c);
        var data = {};
        var s = c.querySelector('[data-events-data]');
        if (s) { try { data = JSON.parse(s.textContent); } catch (e) { data = {}; } }
        c._data = data || {};
        c._search = (c.dataset.eventsSearch || '').toLowerCase();
        c._price = parseFloat(c.dataset.eventsPrice || '0') || 0;
        c._title = (c.dataset.eventsTitle || '').toLowerCase();
        c._date = c.dataset.eventsDate || null;
        c._order = self._orderCounter++;
        self.cards.push(c);
      });
      this.banners = Array.prototype.slice.call(this.grid.querySelectorAll('.card--banner'));
    }

    /* Read the server-rendered filter controls (keys, types) and build a norm->label map for list filters. */
    readControls() {
      var self = this;
      this.groups = Array.prototype.slice.call(this.querySelectorAll('[data-events-filter-group]'));
      this.keys = [];
      this.ftypes = {};
      this.groups.forEach(function (g) {
        var k = g.dataset.key;
        if (self.keys.indexOf(k) === -1) self.keys.push(k);
        self.ftypes[k] = g.dataset.ftype || 'list';
      });
      // Build norm->label map from the server-rendered list checkboxes.
      this.optionLabels = {};
      this.keys.forEach(function (k) { self.optionLabels[k] = {}; });
      Array.prototype.forEach.call(this.querySelectorAll('input[type="checkbox"][name][data-norm]'), function (input) {
        var k = input.name;
        if (!self.optionLabels[k]) self.optionLabels[k] = {};
        var span = input.parentNode.querySelector('span');
        self.optionLabels[k][input.dataset.norm] = span ? span.textContent : input.value;
      });
    }

    /* For collections > 1 page: append any list values found in eager-loaded cards but missing from
       the server-rendered options. */
    supplementOptions() {
      var self = this;
      this.keys.forEach(function (k) {
        if ((self.ftypes[k] || 'list') !== 'list') return;
        var isDate = DATE_KEYS.indexOf(k) !== -1;
        // Collect fresh norms not already present as options.
        var fresh = {};
        self.cards.forEach(function (c) {
          var arr = c._data[k];
          if (!arr) return;
          arr.forEach(function (v) {
            var norm = normalize(v);
            if (norm && self.optionLabels[k][norm] === undefined && fresh[norm] === undefined) {
              fresh[norm] = { value: v, label: isDate ? formatDate(v) : v };
            }
          });
        });
        var norms = Object.keys(fresh);
        if (!norms.length) return;
        norms.forEach(function (norm) { self.optionLabels[k][norm] = fresh[norm].label; });
        // Append to every options list for this key (sidebar, drawer, pills).
        Array.prototype.forEach.call(self.querySelectorAll('[data-events-options][data-key="' + k + '"]'), function (ul) {
          norms.forEach(function (norm) {
            var f = fresh[norm];
            var li = document.createElement('li');
            li.className = 'filter-option';
            li.innerHTML = '<label class="filter-type-default">' +
              '<input type="checkbox" class="checkbox" name="' + escapeAttr(k) + '" value="' + escapeAttr(f.value) + '" data-norm="' + escapeAttr(norm) + '">' +
              '<span>' + escapeHtml(f.label) + '</span></label>';
            ul.appendChild(li);
          });
          // Reveal the group if it was hidden as empty.
          var grp = ul.closest('[data-events-filter-group]');
          if (grp) grp.classList.remove('hide');
        });
      });
    }

    wireEvents() {
      var self = this;

      this.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || !t.matches) return;
        if (t.matches('input[type="checkbox"][name]') && self.keys.indexOf(t.name) !== -1) {
          self.onListChange(t);
        } else if (t.matches('[data-events-range-min],[data-events-range-max]')) {
          self.onRangeChange(t);
        } else if (t.matches('[data-events-daterange-from],[data-events-daterange-to]')) {
          self.onDateRangeChange(t);
        }
      });

      if (this.sortSelect) {
        this.sortSelect.value = this.state.sort;
        this.sortSelect.addEventListener('change', function (e) {
          self.state.sort = e.target.value;
          self.apply();
          self.syncURL();
        });
      }

      if (this.searchInput) {
        this.searchInput.addEventListener('input', debounceFn(function (e) {
          self.state.searchRaw = e.target.value.trim();
          self.state.search = self.state.searchRaw.toLowerCase();
          self.apply();
          self.updateChips();
          self.syncURL();
        }, 200));
      }

      Array.prototype.forEach.call(this.querySelectorAll('[data-events-clear]'), function (b) {
        b.addEventListener('click', function (e) { e.preventDefault(); self.clearAll(); });
      });

      if (this.layout === 'top-pills') {
        Array.prototype.forEach.call(this.querySelectorAll('.events-pill'), function (p) {
          p.addEventListener('toggle', function () {
            if (p.open) {
              Array.prototype.forEach.call(self.querySelectorAll('.events-pill[open]'), function (o) {
                if (o !== p) o.open = false;
              });
            }
          });
        });
        document.addEventListener('click', function (e) {
          if (!e.target.closest || !e.target.closest('.events-pill')) {
            Array.prototype.forEach.call(self.querySelectorAll('.events-pill[open]'), function (o) { o.open = false; });
          }
        });
      }
    }

    onListChange(input) {
      var key = input.name;
      var norm = input.dataset.norm || normalize(input.value);
      if (!this.state.filters[key]) this.state.filters[key] = new Set();
      if (input.checked) this.state.filters[key].add(norm);
      else { this.state.filters[key].delete(norm); if (!this.state.filters[key].size) delete this.state.filters[key]; }
      this.syncInputs(key, norm, input.checked);
      this.apply(); this.updateChips(); this.syncURL();
    }

    onRangeChange(input) {
      var key = input.dataset.key;
      var isMin = input.hasAttribute('data-events-range-min');
      var raw = input.value.trim();
      var val = raw === '' ? null : parseFloat(raw);
      if (val !== null && isNaN(val)) val = null;
      if (!this.state.ranges[key]) this.state.ranges[key] = { min: null, max: null };
      this.state.ranges[key][isMin ? 'min' : 'max'] = val;
      if (this.state.ranges[key].min == null && this.state.ranges[key].max == null) delete this.state.ranges[key];
      // Sync the same role+key across sidebar/drawer/pills.
      var sel = '[data-events-range-' + (isMin ? 'min' : 'max') + '][data-key="' + key + '"]';
      Array.prototype.forEach.call(this.querySelectorAll(sel), function (i) { if (i !== input) i.value = raw; });
      this.apply(); this.updateChips(); this.syncURL();
    }

    onDateRangeChange(input) {
      var key = input.dataset.key;
      var isFrom = input.hasAttribute('data-events-daterange-from');
      var raw = input.value.trim();
      var val = raw === '' ? null : raw;
      if (!this.state.dateRanges[key]) this.state.dateRanges[key] = { from: null, to: null };
      this.state.dateRanges[key][isFrom ? 'from' : 'to'] = val;
      if (!this.state.dateRanges[key].from && !this.state.dateRanges[key].to) delete this.state.dateRanges[key];
      var sel = '[data-events-daterange-' + (isFrom ? 'from' : 'to') + '][data-key="' + key + '"]';
      Array.prototype.forEach.call(this.querySelectorAll(sel), function (i) { if (i !== input) i.value = raw; });
      this.apply(); this.updateChips(); this.syncURL();
    }

    syncInputs(key, norm, checked) {
      Array.prototype.forEach.call(this.querySelectorAll('input[type="checkbox"][name="' + key + '"]'), function (i) {
        if ((i.dataset.norm || normalize(i.value)) === norm) i.checked = checked;
      });
    }

    comparator(sort) {
      switch (sort) {
        case 'date-asc':
          return function (a, b) { var av = a._date || '9999-12-31', bv = b._date || '9999-12-31'; return av < bv ? -1 : av > bv ? 1 : a._order - b._order; };
        case 'date-desc':
          return function (a, b) { var av = a._date || '0000-01-01', bv = b._date || '0000-01-01'; return av > bv ? -1 : av < bv ? 1 : a._order - b._order; };
        case 'price-asc':
          return function (a, b) { return (a._price - b._price) || (a._order - b._order); };
        case 'price-desc':
          return function (a, b) { return (b._price - a._price) || (a._order - b._order); };
        case 'title-asc':
          return function (a, b) { return a._title.localeCompare(b._title) || (a._order - b._order); };
        case 'title-desc':
          return function (a, b) { return b._title.localeCompare(a._title) || (a._order - b._order); };
        default:
          return function (a, b) { return a._order - b._order; };
      }
    }

    reorder() {
      var ordered = this.cards.slice().sort(this.comparator(this.state.sort));
      var frag = document.createDocumentFragment();
      if (!this.banners.length) {
        ordered.forEach(function (el) { frag.appendChild(el); });
        this.grid.appendChild(frag);
        return;
      }
      var children = Array.prototype.slice.call(this.grid.children);
      var oi = 0;
      children.forEach(function (el) {
        if (el.classList.contains('card--banner')) frag.appendChild(el);
        else if (oi < ordered.length) frag.appendChild(ordered[oi++]);
      });
      while (oi < ordered.length) frag.appendChild(ordered[oi++]);
      this.grid.appendChild(frag);
    }

    cardMatches(c, activeList, activeRange, activeDate) {
      var i, k;
      for (i = 0; i < activeList.length; i++) {
        k = activeList[i];
        var set = this.state.filters[k];
        var arr = c._data[k] || [];
        var hit = arr.some(function (v) { return set.has(normalize(v)); });
        if (!hit) return false;
      }
      for (i = 0; i < activeRange.length; i++) {
        k = activeRange[i];
        var r = this.state.ranges[k];
        var nums = (c._data[k] || []).map(Number).filter(function (n) { return !isNaN(n); });
        var rhit = nums.some(function (n) {
          return (r.min == null || n >= r.min) && (r.max == null || n <= r.max);
        });
        if (!rhit) return false;
      }
      for (i = 0; i < activeDate.length; i++) {
        k = activeDate[i];
        var dr = this.state.dateRanges[k];
        var ds = c._data[k] || [];
        var dhit = ds.some(function (d) {
          return (!dr.from || d >= dr.from) && (!dr.to || d <= dr.to);
        });
        if (!dhit) return false;
      }
      return true;
    }

    apply() {
      var self = this;
      var activeList = Object.keys(this.state.filters).filter(function (k) { return self.state.filters[k] && self.state.filters[k].size; });
      var activeRange = Object.keys(this.state.ranges);
      var activeDate = Object.keys(this.state.dateRanges);
      var search = this.state.search;

      this.reorder();

      var visible = 0;
      this.cards.forEach(function (c) {
        var ok = self.cardMatches(c, activeList, activeRange, activeDate);
        if (ok && search && c._search.indexOf(search) === -1) ok = false;
        c.classList.toggle('hide', !ok);
        if (ok) visible++;
      });

      var filtering = activeList.length > 0 || activeRange.length > 0 || activeDate.length > 0 || search !== '';
      this.banners.forEach(function (b) { b.classList.toggle('hide', filtering); });

      this.updateCount(visible);
      if (this.emptyEl) this.emptyEl.classList.toggle('hide', visible !== 0);
      this.updatePillCounts();
    }

    updateCount(n) {
      if (!this.countEl) return;
      var ts = window.themeStrings, txt;
      if (ts && ts.product_count_simple_one && ts.product_count_simple_other) {
        txt = (n === 1 ? ts.product_count_simple_one : ts.product_count_simple_other).replace('[count]', n);
      } else {
        txt = n + (n === 1 ? ' result' : ' results');
      }
      this.countEl.textContent = txt;
    }

    groupLabel(key) {
      var g = this.querySelector('[data-events-filter-group][data-key="' + key + '"]');
      return g && g.dataset.label ? g.dataset.label : key;
    }

    chipButton(label, onRemove) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn btn--with-icon size--tiny events-chip';
      btn.innerHTML = '<span>' + escapeHtml(label) + '</span>' +
        '<svg role="presentation" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
      btn.addEventListener('click', onRemove);
      return btn;
    }

    updateChips() {
      if (!this.chipsEl) return;
      var self = this;
      var frag = document.createDocumentFragment();

      Object.keys(this.state.filters).forEach(function (key) {
        var set = self.state.filters[key];
        if (!set || !set.size) return;
        var label = self.groupLabel(key);
        set.forEach(function (norm) {
          var disp = (self.optionLabels[key] && self.optionLabels[key][norm]) || norm;
          frag.appendChild(self.chipButton(label + ': ' + disp, function () { self.removeListFilter(key, norm); }));
        });
      });

      Object.keys(this.state.ranges).forEach(function (key) {
        var r = self.state.ranges[key];
        var label = self.groupLabel(key);
        var txt = (r.min != null ? r.min : '–') + ' to ' + (r.max != null ? r.max : '–');
        frag.appendChild(self.chipButton(label + ': ' + txt, function () { self.removeRange(key); }));
      });

      Object.keys(this.state.dateRanges).forEach(function (key) {
        var dr = self.state.dateRanges[key];
        var label = self.groupLabel(key);
        var txt = (dr.from ? formatDate(dr.from) : '–') + ' to ' + (dr.to ? formatDate(dr.to) : '–');
        frag.appendChild(self.chipButton(label + ': ' + txt, function () { self.removeDateRange(key); }));
      });

      this.chipsEl.innerHTML = '';
      this.chipsEl.appendChild(frag);

      var any = Object.keys(this.state.filters).some(function (k) { return self.state.filters[k] && self.state.filters[k].size; }) ||
        Object.keys(this.state.ranges).length > 0 || Object.keys(this.state.dateRanges).length > 0 || this.state.search !== '';
      Array.prototype.forEach.call(this.querySelectorAll('.events-clear'), function (b) { b.classList.toggle('hide', !any); });
    }

    updatePillCounts() {
      var self = this;
      Array.prototype.forEach.call(this.querySelectorAll('.events-pill[data-events-filter-group]'), function (p) {
        var key = p.dataset.key;
        var n = 0;
        if (self.state.filters[key]) n += self.state.filters[key].size;
        if (self.state.ranges[key]) n += 1;
        if (self.state.dateRanges[key]) n += 1;
        var c = p.querySelector('[data-events-pill-count]');
        if (c) c.textContent = n ? '(' + n + ')' : '';
        p.classList.toggle('has-selection', n > 0);
      });
    }

    removeListFilter(key, norm) {
      var set = this.state.filters[key];
      if (set) { set.delete(norm); if (!set.size) delete this.state.filters[key]; }
      this.syncInputs(key, norm, false);
      this.apply(); this.updateChips(); this.syncURL();
    }

    removeRange(key) {
      delete this.state.ranges[key];
      Array.prototype.forEach.call(this.querySelectorAll('[data-events-range-min][data-key="' + key + '"],[data-events-range-max][data-key="' + key + '"]'), function (i) { i.value = ''; });
      this.apply(); this.updateChips(); this.syncURL();
    }

    removeDateRange(key) {
      delete this.state.dateRanges[key];
      Array.prototype.forEach.call(this.querySelectorAll('[data-events-daterange-from][data-key="' + key + '"],[data-events-daterange-to][data-key="' + key + '"]'), function (i) { i.value = ''; });
      this.apply(); this.updateChips(); this.syncURL();
    }

    reassertChecked() {
      var self = this;
      Object.keys(this.state.filters).forEach(function (key) {
        self.state.filters[key].forEach(function (norm) { self.syncInputs(key, norm, true); });
      });
    }

    clearAll() {
      var self = this;
      this.state.filters = {};
      this.state.ranges = {};
      this.state.dateRanges = {};
      this.state.search = '';
      this.state.searchRaw = '';
      Array.prototype.forEach.call(this.querySelectorAll('input[type="checkbox"][name]'), function (i) { if (self.keys.indexOf(i.name) !== -1) i.checked = false; });
      Array.prototype.forEach.call(this.querySelectorAll('[data-events-range-min],[data-events-range-max],[data-events-daterange-from],[data-events-daterange-to]'), function (i) { i.value = ''; });
      if (this.searchInput) this.searchInput.value = '';
      this.apply(); this.updateChips(); this.syncURL();
    }

    readURL() {
      var self = this;
      var params = new URLSearchParams(location.search);

      var q = params.get('ev_q');
      if (q !== null) { this.state.searchRaw = q; this.state.search = q.toLowerCase(); if (this.searchInput) this.searchInput.value = q; }
      var s = params.get('ev_sort');
      if (s) { this.state.sort = s; if (this.sortSelect) this.sortSelect.value = s; }

      this.keys.forEach(function (k) {
        var ftype = self.ftypes[k] || 'list';
        if (ftype === 'range') {
          var mn = params.get('ev_' + k + '__min'), mx = params.get('ev_' + k + '__max');
          if (mn !== null || mx !== null) {
            self.state.ranges[k] = { min: mn !== null ? parseFloat(mn) : null, max: mx !== null ? parseFloat(mx) : null };
            if (mn !== null) Array.prototype.forEach.call(self.querySelectorAll('[data-events-range-min][data-key="' + k + '"]'), function (i) { i.value = mn; });
            if (mx !== null) Array.prototype.forEach.call(self.querySelectorAll('[data-events-range-max][data-key="' + k + '"]'), function (i) { i.value = mx; });
          }
        } else if (ftype === 'date_range') {
          var fr = params.get('ev_' + k + '__from'), to = params.get('ev_' + k + '__to');
          if (fr !== null || to !== null) {
            self.state.dateRanges[k] = { from: fr, to: to };
            if (fr !== null) Array.prototype.forEach.call(self.querySelectorAll('[data-events-daterange-from][data-key="' + k + '"]'), function (i) { i.value = fr; });
            if (to !== null) Array.prototype.forEach.call(self.querySelectorAll('[data-events-daterange-to][data-key="' + k + '"]'), function (i) { i.value = to; });
          }
        } else {
          var raw = params.get('ev_' + k);
          if (!raw) return;
          raw.split(',').forEach(function (v) {
            var norm = normalize(decodeURIComponent(v));
            if (!norm) return;
            if (!self.state.filters[k]) self.state.filters[k] = new Set();
            self.state.filters[k].add(norm);
            self.syncInputs(k, norm, true);
          });
        }
      });
    }

    syncURL() {
      var self = this;
      var params = new URLSearchParams();
      if (this.state.searchRaw) params.set('ev_q', this.state.searchRaw);
      if (this.state.sort && this.state.sort !== this.defaultSort) params.set('ev_sort', this.state.sort);
      Object.keys(this.state.filters).forEach(function (k) {
        var set = self.state.filters[k];
        if (set && set.size) params.set('ev_' + k, Array.prototype.slice.call(set).map(encodeURIComponent).join(','));
      });
      Object.keys(this.state.ranges).forEach(function (k) {
        var r = self.state.ranges[k];
        if (r.min != null) params.set('ev_' + k + '__min', r.min);
        if (r.max != null) params.set('ev_' + k + '__max', r.max);
      });
      Object.keys(this.state.dateRanges).forEach(function (k) {
        var dr = self.state.dateRanges[k];
        if (dr.from) params.set('ev_' + k + '__from', dr.from);
        if (dr.to) params.set('ev_' + k + '__to', dr.to);
      });
      var qs = params.toString();
      try { history.replaceState({}, '', location.pathname + (qs ? '?' + qs : '')); } catch (e) { /* noop */ }
    }
  }

  if (customElements.get('events-grid') === undefined) {
    customElements.define('events-grid', EventsGrid);
  }
})();
